import { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { AppContext } from '../../../context/AppContext';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Modal, Form, Spinner, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { payoutProcessSchema } from '../../../schemas/wallet/admin.schema';
import DataTable from '../../../component/wallet/data-table/DataTable';
import DataTableToolbar from '../../../component/wallet/data-table/DataTableToolbar';
import StatusBadge from '../../../component/wallet/StatusBadge';
import CurrencyDisplay from '../../../component/wallet/CurrencyDisplay';
import ConfirmDialog from '../../../component/wallet/ConfirmDialog';
import { useAdmin } from '../../../Hooks/wallet/useadmin';
import { formatDate } from '../../../lib/wallet/utils';
import { PAYOUT_STATUSES, PAYOUT_REASONS, DEFAULT_PAGE_SIZE } from '../../../lib/wallet/constants';

const columnHelper = createColumnHelper();

const PayoutsPage = () => {
    const { sidebar, permissions } = useContext(AppContext);
    const { loading, fetchPayouts, doApprovePayout, doProcessPayout } = useAdmin();
    const [payouts, setPayouts] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: DEFAULT_PAGE_SIZE, total_count: 0, total_pages: 1 });
    const [statusFilter, setStatusFilter] = useState('');
    const [reasonFilter, setReasonFilter] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [showProcess, setShowProcess] = useState(false);
    const [processingId, setProcessingId] = useState(null);

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        resolver: zodResolver(payoutProcessSchema),
    });

    const loadPayouts = useCallback(
        async (page = 1) => {
            try {
                const res = await fetchPayouts({
                    page,
                    limit: pagination.limit,
                    status: statusFilter || undefined,
                    reason: reasonFilter || undefined,
                    from_date: fromDate || undefined,
                    to_date: toDate || undefined,
                });
                setPayouts(res?.data?.payouts || []);
                setPagination((prev) => ({ ...prev, page, ...res?.data?.pagination }));
            } catch {
                // handled
            }
        },
        [pagination.limit, statusFilter, reasonFilter, fromDate, toDate, fetchPayouts]
    );

    useEffect(() => {
        loadPayouts(1);
    }, [statusFilter, reasonFilter, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleApprove = async (id) => {
        const confirmed = await ConfirmDialog({
            title: 'Approve Payout',
            message: `Approve payout #${id}?`,
        });
        if (!confirmed) return;
        try {
            await doApprovePayout(id);
            loadPayouts(pagination.page);
        } catch {
            // handled
        }
    };

    const handleProcess = (id) => {
        setProcessingId(id);
        setShowProcess(true);
    };

    const onProcessSubmit = async (data) => {
        try {
            await doProcessPayout(processingId, data);
            setShowProcess(false);
            reset();
            loadPayouts(pagination.page);
        } catch {
            // handled
        }
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor('id', { header: 'ID', size: 60 }),
            columnHelper.accessor('user_name', { header: 'User' }),
            columnHelper.accessor('amount', {
                header: 'Amount',
                cell: (info) => (
                    <CurrencyDisplay amount={info.getValue()} currencySymbol={info.row.original.currency_symbol} />
                ),
            }),
            columnHelper.accessor('payout_method', {
                header: 'Method',
                cell: (info) => <span className="text-capitalize">{info.getValue()?.replace(/_/g, ' ')}</span>,
            }),
            columnHelper.accessor('reason', {
                header: 'Reason',
                cell: (info) => <span className="text-capitalize">{info.getValue()?.replace(/_/g, ' ')}</span>,
            }),
            columnHelper.accessor('status', {
                header: 'Status',
                cell: (info) => <StatusBadge status={info.getValue()} />,
            }),
            columnHelper.accessor('created_at', {
                header: 'Created',
                cell: (info) => formatDate(info.getValue()),
            }),
             permissions && permissions["Manage Wallet Admin Payouts"] && (
            columnHelper.display({
                id: 'actions',
                header: 'Actions',
                cell: ({ row }) => {
                    const { status, id } = row.original;
                    return (
                        <div className="d-flex gap-1">
                            {status === 'pending' && (
                                <Button size="sm"  onClick={() => handleApprove(id)}>
                                    Approve
                                </Button>
                            )}
                            {status === 'approved' && (
                                <Button size="sm" onClick={() => handleProcess(id)}>
                                    Process
                                </Button>
                            )}
                        </div>
                    );
                },
            })
        )
        ].filter(Boolean),
        [permissions] // eslint-disable-line react-hooks/exhaustive-deps
    );

    return (
        <div className={sidebar ? "taskMain " : "cx-active taskMain"} id="cx-main">
            <div className="role-content-main">
            <div className="sub-header-role mb-4">
                <h6 className="mt-2 role-text">Payouts</h6>
            </div>
                <div className="fade-in">
            

            <DataTableToolbar
                filters={[
                    { value: statusFilter, onChange: setStatusFilter, placeholder: 'All Statuses', options: PAYOUT_STATUSES },
                    { value: reasonFilter, onChange: setReasonFilter, placeholder: 'All Reasons', options: PAYOUT_REASONS },
                ]}
            >
                <Col xs={6} md={2}>
                    <Form.Control size="sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </Col>
                <Col xs={6} md={2}>
                    <Form.Control size="sm" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </Col>
            </DataTableToolbar>

            <DataTable
                columns={columns}
                data={payouts}
                loading={loading}
                page={pagination.page}
                pageSize={pagination.limit}
                totalCount={pagination.total_count}
                totalPages={pagination.total_pages}
                onPageChange={(p) => loadPayouts(p)}
                onPageSizeChange={(s) => setPagination((prev) => ({ ...prev, limit: s }))}
                emptyTitle="No payouts"
                emptyDescription="No payouts found."
            />

            {/* Process Modal */}
            <Modal show={showProcess} onHide={() => setShowProcess(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Process Payout #{processingId}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit(onProcessSubmit)}>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Label>Payout Reference</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., BANK-TXN-20260218-001"
                                isInvalid={!!errors.payout_reference}
                                {...register('payout_reference')}
                            />
                            <Form.Control.Feedback type="invalid">{errors.payout_reference?.message}</Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => setShowProcess(false)}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={loading}>
                            {loading ? <Spinner size="sm" /> : 'Process'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
        </div>
        </div>
    );
};

export default PayoutsPage;
